<template>
  <FormSection id="description" required>
    <div class="mt-4">
      <FlagLanguageDisplay country-code="en" class="mb-2" />
      <NovaTextEditor
        id="editorial-description"
        :model-value="curationValues.text1"
        :placeholder="$t('experience.description.editor.placeholder')"
        :readonly="true"
      >
        <DiffHtml v-if="hasDiff" :old-value="diffValues?.text1 ?? ''" :value="curationValues.text1 ?? ''" />
      </NovaTextEditor>
    </div>

    <div class="mt-6">
      <FlagLanguageDisplay :country-code="language" class="mb-2" />

      <NovaTextEditor
        id="translation-description"
        v-model="formValues.text1"
        class=""
        :placeholder="$t('experience.description.editor.placeholder')"
        :error="$t(getFieldErrors('text1') || '')"
        :readonly="readonly"
      />
    </div>
  </FormSection>

  <FormSection id="seo_description" required>
    <div class="mt-4">
      <FlagLanguageDisplay country-code="en" class="mb-2" />
      <NovaTextEditor
        id="editorial-seo-description"
        class=""
        :model-value="curationValues.seo_description"
        :placeholder="$t('experience.seo_description.title')"
        :readonly="true"
      >
        <DiffHtml
          v-if="hasDiff"
          :old-value="diffValues?.seo_description ?? ''"
          :value="curationValues.seo_description ?? ''"
        />
      </NovaTextEditor>
    </div>

    <div class="mt-6">
      <FlagLanguageDisplay :country-code="language" class="mb-2" />

      <NovaTextEditor
        id="translation-seo-description"
        v-model="formValues.seo_description"
        class=""
        :placeholder="$t('experience.seo_description.title')"
        :readonly="readonly"
      />
    </div>
  </FormSection>
  <FormSection v-if="useFeatureFlag('showAdditionalDescription')" id="additional_description">
    <div class="mt-4">
      <FlagLanguageDisplay country-code="en" class="mb-2" />
      <NovaTextEditor
        id="editorial-additional-description"
        class=""
        :model-value="curationValues.text2 ?? ''"
        :placeholder="$t('experience.additional_description.title')"
        :readonly="true"
      >
        <DiffHtml v-if="hasDiff" :old-value="diffValues?.text2 ?? ''" :value="curationValues.text2 ?? ''" />
      </NovaTextEditor>
    </div>

    <div class="mt-6">
      <FlagLanguageDisplay :country-code="language" class="mb-2" />
      <NovaTextEditor
        id="translation-additional-description"
        v-model="formValues.text2"
        class=""
        :placeholder="$t('experience.additional_description.title')"
        :readonly="readonly"
      />
    </div>
  </FormSection>
  <FormSection v-if="useHighlightsV2 && isRevision" id="highlights" :required="true">
    <TranslationHighlightsV2
      :custom-rows="revisionRows.highlights.custom"
      data-testid="translation-highlights"
      :premade-rows="revisionRows.highlights.premade"
      :target-country-code="language"
      readonly
    />
  </FormSection>
  <TranslationContentHighlightsV2 v-else-if="useHighlightsV2" />
  <FormSection v-else id="highlights" :required="true">
    <div class="TranslationHighlightsSwitch">
      <span class="mr-4">
        {{ $t("experience.highlights.premade-content") }}
      </span>
      <NovaSwitch v-model="showPremade[0].value" />
    </div>
    <div class="DocumentForm__horizontal-container">
      <div>
        <FlagLanguageDisplay country-code="en" class="mb-2" />
        <TranslationHighlights
          :model-value="curationValues.custom_highlights"
          data-testid="editorial-highlights"
          :premade-items="curationValues.premade_highlights"
          :disabled="true"
          :show-premade="showPremade[0].value"
          :show-diff="hasDiff"
          :diff-old-value="{
            custom: diffValues?.custom_highlights ?? [],
            premade: diffValues?.premade_highlights ?? [],
          }"
          :diff-new-value="{ custom: curationValues.custom_highlights, premade: curationValues.premade_highlights }"
        />
      </div>
      <div>
        <FlagLanguageDisplay :country-code="language" class="mb-2" />
        <TranslationHighlights
          v-model="formValues.custom_highlights"
          data-testid="translation-highlights"
          :premade-items="initialValues.premade_highlights"
          :show-premade="showPremade[0].value"
          :disabled="readonly"
        />
      </div>
    </div>
  </FormSection>
  <FormSection v-if="useHighlightsV2 && isRevision" id="included" :required="true">
    <TranslationHighlightsV2
      :custom-rows="revisionRows.included.custom"
      data-testid="translation-included"
      :premade-rows="revisionRows.included.premade"
      :target-country-code="language"
      readonly
    />
  </FormSection>
  <TranslationContentIncludedV2 v-else-if="useHighlightsV2" />
  <FormSection v-else id="included" :required="true">
    <div class="TranslationHighlightsSwitch">
      <span class="mr-4">
        {{ $t("experience.highlights.premade-content") }}
      </span>
      <NovaSwitch v-model="showPremade[1].value" />
    </div>
    <div class="DocumentForm__horizontal-container">
      <div>
        <FlagLanguageDisplay country-code="en" class="mb-2" />
        <TranslationHighlights
          :model-value="curationValues.custom_included"
          data-testid="editorial-included"
          :premade-items="curationValues.premade_included"
          :disabled="true"
          :show-premade="showPremade[1].value"
          :show-diff="hasDiff"
          :diff-old-value="{ custom: diffValues?.custom_included ?? [], premade: diffValues?.premade_included ?? [] }"
          :diff-new-value="{ custom: curationValues.custom_included, premade: curationValues.premade_included }"
        />
      </div>
      <div>
        <FlagLanguageDisplay :country-code="language" class="mb-2" />
        <TranslationHighlights
          v-model="formValues.custom_included"
          data-testid="translation-included"
          :premade-items="initialValues.premade_included"
          :show-premade="showPremade[1].value"
          :disabled="readonly"
        />
      </div>
    </div>
  </FormSection>
  <FormSection v-if="useHighlightsV2 && isRevision" id="non_included">
    <TranslationHighlightsV2
      :custom-rows="revisionRows.non_included.custom"
      data-testid="translation-non_included"
      :premade-rows="revisionRows.non_included.premade"
      :target-country-code="language"
      readonly
    />
  </FormSection>
  <TranslationContentNonIncludedV2 v-else-if="useHighlightsV2" />
  <FormSection v-else id="non_included">
    <div class="TranslationHighlightsSwitch">
      <span class="mr-4">
        {{ $t("experience.highlights.premade-content") }}
      </span>
      <NovaSwitch v-model="showPremade[2].value" />
    </div>
    <div class="DocumentForm__horizontal-container">
      <div>
        <FlagLanguageDisplay country-code="en" class="mb-2" />
        <TranslationHighlights
          :model-value="curationValues.custom_non_included"
          data-testid="editorial-non_included"
          :premade-items="curationValues.premade_non_included"
          :disabled="true"
          :show-premade="showPremade[2].value"
          :show-diff="hasDiff"
          :diff-old-value="{
            custom: diffValues?.custom_non_included ?? [],
            premade: diffValues?.premade_non_included ?? [],
          }"
          :diff-new-value="{
            custom: curationValues.custom_non_included,
            premade: curationValues.premade_non_included,
          }"
        />
      </div>
      <div>
        <FlagLanguageDisplay :country-code="language" class="mb-2" />
        <TranslationHighlights
          v-model="formValues.custom_non_included"
          data-testid="translation-non_included"
          :premade-items="initialValues.premade_non_included"
          :show-premade="showPremade[2].value"
          :disabled="readonly"
        />
      </div>
    </div>
  </FormSection>
  <FormSection v-if="useHighlightsV2 && isRevision" id="important_information">
    <TranslationHighlightsV2
      :custom-rows="revisionRows.important_information.custom"
      data-testid="translation-important_information"
      :premade-rows="revisionRows.important_information.premade"
      :target-country-code="language"
      readonly
    />
  </FormSection>
  <TranslationContentImportantInformationV2 v-else-if="useHighlightsV2" />
  <FormSection v-else id="important_information">
    <div class="TranslationHighlightsSwitch">
      <span class="mr-4">
        {{ $t("experience.highlights.premade-content") }}
      </span>
      <NovaSwitch v-model="showPremade[3].value" />
    </div>
    <div class="DocumentForm__horizontal-container">
      <div>
        <FlagLanguageDisplay country-code="en" class="mb-2" />
        <TranslationHighlights
          :model-value="curationValues.custom_important_information"
          data-testid="editorial-important_information"
          :premade-items="curationValues.premade_important_information"
          :disabled="true"
          :show-premade="showPremade[3].value"
          :show-diff="hasDiff"
          :diff-old-value="{
            custom: diffValues?.custom_important_information ?? [],
            premade: diffValues?.premade_important_information ?? [],
          }"
          :diff-new-value="{
            custom: curationValues.custom_important_information,
            premade: curationValues.premade_important_information,
          }"
        />
      </div>
      <div>
        <FlagLanguageDisplay :country-code="language" class="mb-2" />
        <TranslationHighlights
          v-model="formValues.custom_important_information"
          data-testid="translation-important_information"
          :premade-items="initialValues.premade_important_information"
          :show-premade="showPremade[3].value"
          :disabled="readonly"
        />
      </div>
    </div>
  </FormSection>
</template>

<script setup lang="ts">
import FormSection from "@/components/Document/FormSection/FormSection.vue";
import { useFormValidation } from "@/features/experience-translation/composables/useFormValidation";
import { useFormValues } from "@/features/experience-translation/composables/useFormValues";
import { GenericHighlight } from "@/types/Highlights";
import NovaSwitch from "@/ui-kit/NovaSwitch/NovaSwitch.vue";
import NovaTextEditor from "@/ui-kit/NovaTextEditor/NovaTextEditor.vue";
import { z } from "zod/v4";
import FlagLanguageDisplay from "../components/FlagLanguageDisplay.vue";
import TranslationHighlights from "../components/TranslationHighlights.vue";
import TranslationContentHighlightsV2 from "../sections/content/highlights/TranslationHighlights.vue";
import TranslationContentIncludedV2 from "../sections/content/included/TranslationIncluded.vue";
import TranslationContentNonIncludedV2 from "../sections/content/non-included/TranslationNonIncluded.vue";
import TranslationContentImportantInformationV2 from "../sections/content/important-information/TranslationImportantInformation.vue";
import TranslationHighlightsV2 from "@/features/experience-shared/highlights-v2/translation/components/TranslationHighlightsV2.vue";
import {
  toPremadeRows,
  toCustomRows,
} from "@/features/experience-shared/highlights-v2/translation/utils/translation-highlight-mappers";
import DiffHtml from "@/features/experience-shared/components/DiffHtml.vue";
import { TranslationFormProps } from "../types";
import { useFeatureFlag } from "@/features/experience-shared/composables/useFeatureFlag";

export interface TranslationContentGenerationValues {
  text1?: string;
  seo_description?: string;
  text2?: string;
  custom_highlights: GenericHighlight[];
  custom_included: GenericHighlight[];
  custom_non_included: GenericHighlight[];
  custom_important_information: GenericHighlight[];
  premade_highlights: GenericHighlight[];
  premade_important_information: GenericHighlight[];
  premade_included: GenericHighlight[];
  premade_non_included: GenericHighlight[];
}

type Props = TranslationFormProps<TranslationContentGenerationValues>;
interface Events {
  (e: "update:modelValue", update: TranslationContentGenerationValues): void;
}

const props = defineProps<Props>();
const emits = defineEmits<Events>();

const formSchema = z.object({
  text1: z.string(),
  seo_description: z.string(),
  text2: z.string().optional(),
});

const { formValues } = useFormValues(props.initialValues, handleUpdate);
const { getFieldErrors, runValidation } = useFormValidation(formValues, formSchema, { immediate: true });

const useHighlightsV2 = useFeatureFlag("use_highlights_v2");

const showPremade = [ref(true), ref(true), ref(true), ref(true)];

// --- Revision rendering ---------------------------------------------------------------------
// The translation revision flow reuses THIS editable form (unlike raw/curation, which have their
// own dedicated `Revision*Form` components). In revision there is no live translation store: the
// revision page loads everything into `useRevisionStore` and hands it to the form via props
// (`initialValues` = the translated snapshot, `curationValues` = the English/source snapshot).
//
// That is why the store-driven V2 sections can't be used here — they read the per-field stores,
// which in revision are never loaded, so the target column would render blank. Instead, when
// `isRevision` is set we mount `TranslationHighlightsV2` directly, read-only, and project its
// rows straight from the props with the same pure mappers the stores use: source column from the
// curation (English) list, target column from the translated list. No store access.
const revisionRows = computed(() => ({
  highlights: {
    premade: toPremadeRows(props.curationValues.premade_highlights, props.initialValues.premade_highlights),
    custom: toCustomRows(props.curationValues.custom_highlights, props.initialValues.custom_highlights),
  },
  included: {
    premade: toPremadeRows(props.curationValues.premade_included, props.initialValues.premade_included),
    custom: toCustomRows(props.curationValues.custom_included, props.initialValues.custom_included),
  },
  non_included: {
    premade: toPremadeRows(props.curationValues.premade_non_included, props.initialValues.premade_non_included),
    custom: toCustomRows(props.curationValues.custom_non_included, props.initialValues.custom_non_included),
  },
  important_information: {
    premade: toPremadeRows(
      props.curationValues.premade_important_information,
      props.initialValues.premade_important_information
    ),
    custom: toCustomRows(
      props.curationValues.custom_important_information,
      props.initialValues.custom_important_information
    ),
  },
}));

function handleUpdate() {
  runValidation();
  emits("update:modelValue", formValues.value);
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/utilities";

.TranslationHighlightsSwitch {
  display: flex;
  margin-top: rem(24);
  margin-bottom: rem(24);

  @include font-regular(12);
}
</style>
